import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pause, Play, Power, RotateCcw, Square, Terminal, X } from "lucide-react";
import { api, errMsg, fmtTs, inr, num, pct, tone } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const Stat = ({ label, value, cls = "text-slate-100", tid }) => (
  <div className="panel p-3">
    <div className="widget-title">{label}</div>
    <div className={`font-mono text-lg mt-1 ${cls}`} data-testid={tid}>{value}</div>
  </div>
);

const LOG_COLOR = { BUY: "text-bull", SELL: "text-bear", WARN: "text-amber-400", ERROR: "text-bear", INFO: "text-slate-400" };

const Panel = ({ title, right, children, tid }) => (
  <div className="panel min-w-0" data-testid={tid}>
    <div className="flex items-center justify-between px-3 py-2 border-b border-[#163056] min-h-[40px]">
      <span className="widget-title">{title}</span>{right}
    </div>
    {children}
  </div>
);

export default function PaperTrading({ account, reload }) {
  const { isAdmin } = useAuth();
  const [bots, setBots] = useState([]);
  const [orders, setOrders] = useState([]);
  const [logs, setLogs] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [sel, setSel] = useState("");

  const load = useCallback(async () => {
    const [b, o, l] = await Promise.all([api.get("/paper/bots"), api.get("/paper/orders"), api.get("/paper/logs")]);
    setBots(b.data); setOrders(o.data); setLogs(l.data);
  }, []);

  useEffect(() => {
    load();
    api.get(isAdmin ? "/strategies" : "/catalog").then((r) => { setStrategies(r.data); if (r.data[0]) setSel(r.data[0].id); });
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [load, isAdmin]);

  const act = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); await Promise.all([load(), reload()]); } catch (e) { toast.error(errMsg(e)); }
  };
  const a = account || {};
  const liveBots = bots.filter((b) => b.mode === "LIVE").length;
  const kill = () => window.confirm(`KILL SWITCH: stop ${liveBots} live bot(s) and square off all live positions in your AliceBlue account?`) && act(() => api.post("/live/kill"), "Kill switch executed");

  return (
    <div className="space-y-3 rise" data-testid="paper-trading">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-xl font-bold text-white tracking-tight">Bots & Trading</h1>
        <span className="font-mono text-[10px] px-2 py-0.5 border border-amber-500/40 text-amber-400 rounded-sm">PAPER · ₹10L VIRTUAL</span>
        {liveBots > 0 && <span className="font-mono text-[10px] px-2 py-0.5 border border-[#FF3B30]/50 text-[#FF3B30] rounded-sm pulse-dot" data-testid="live-bots-badge">{liveBots} LIVE BOT{liveBots > 1 ? "S" : ""} · REAL ORDERS</span>}
        <button data-testid="kill-switch-button" onClick={kill} disabled={!liveBots} className="btn-bear h-8 ml-auto"><Power className="w-3.5 h-3.5" /> Kill Switch</button>
        <button data-testid="paper-reset-button" onClick={() => window.confirm("Reset paper account? Bots, positions & orders will be cleared.") && act(() => api.post("/paper/reset"), "Account reset")}
          className="btn-ghost"><RotateCcw className="w-3.5 h-3.5" /> Reset Account</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Equity" tid="paper-equity" value={inr(a.equity)} />
        <Stat label="Available Cash" tid="paper-cash" value={inr(a.cash)} />
        <Stat label="Realized P&L" tid="paper-realized-pnl" value={inr(a.realized_pnl)} cls={tone(a.realized_pnl)} />
        <Stat label="Unrealized P&L" tid="paper-unrealized-pnl" value={inr(a.unrealized_pnl)} cls={tone(a.unrealized_pnl)} />
        <Stat label="Total P&L" tid="paper-trading-pnl" value={inr(a.total_pnl)} cls={tone(a.total_pnl)} />
      </div>
      {(a.live_total_pnl !== 0 || liveBots > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="live-stats">
          <Stat label="Live Realized P&L" tid="live-realized-pnl" value={inr(a.live_realized_pnl)} cls={tone(a.live_realized_pnl)} />
          <Stat label="Live Unrealized P&L" tid="live-unrealized-pnl" value={inr(a.live_unrealized_pnl)} cls={tone(a.live_unrealized_pnl)} />
          <Stat label="Live Total P&L" tid="live-total-pnl" value={inr(a.live_total_pnl)} cls={tone(a.live_total_pnl)} />
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-3">
        <Panel tid="bots-panel" title={`Strategy Bots (${bots.length})`} right={
          <div className="flex gap-2">
            <select data-testid="bot-strategy-select" className="field max-w-[220px]" value={sel} onChange={(e) => setSel(e.target.value)}>
              {strategies.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button data-testid="bot-deploy-button" disabled={!sel} onClick={() => act(() => api.post("/paper/bots", { strategy_id: sel, mode: "PAPER" }), "Paper bot deployed")} className="btn-bull h-8">
              <Play className="w-3.5 h-3.5" /> Deploy Paper
            </button>
          </div>}>
          <table className="tbl w-full">
            <thead><tr><th>Bot</th><th>Mode</th><th>Symbol</th><th>TF</th><th>Status</th><th>Trades</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {bots.map((b) => (
                <tr key={b.id} data-testid={`bot-row-${b.id}`}>
                  <td className="font-sans text-slate-100">{b.name}</td>
                  <td><span data-testid={`bot-mode-${b.id}`} className={`text-[10px] font-mono px-1.5 py-0.5 border rounded-sm ${b.mode === "LIVE" ? "text-[#FF3B30] border-[#FF3B30]/50" : "text-amber-400 border-amber-500/40"}`}>{b.mode || "PAPER"}</span>{b.mode === "LIVE" && b.max_capital && <span className="text-[10px] text-slate-500 ml-1">₹{Number(b.max_capital).toLocaleString("en-IN")}</span>}</td>
                  <td>{b.symbol}</td><td>{b.timeframe}</td>
                  <td>
                    <span className={`inline-flex items-center gap-1.5 text-[10px] ${b.status === "ACTIVE" ? "text-bull" : "text-amber-400"}`} data-testid={`bot-status-${b.id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${b.status === "ACTIVE" ? "bg-[#00E676] pulse-dot" : "bg-amber-400"}`} />{b.status}
                    </span>
                  </td>
                  <td>{b.trades}</td>
                  <td className="text-right space-x-1">
                    <button data-testid={`bot-toggle-${b.id}`} onClick={() => act(() => api.post(`/paper/bots/${b.id}/toggle`))} className="btn-ghost h-6 px-2">
                      {b.status === "ACTIVE" ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                    </button>
                    <button data-testid={`bot-stop-${b.id}`} onClick={() => act(() => api.delete(`/paper/bots/${b.id}`), "Bot stopped")} className="btn-ghost h-6 px-2 hover:text-[#FF3B30]">
                      <Square className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
              {!bots.length && <tr><td colSpan={7} className="text-slate-500 font-sans py-6 text-center">No bots running. Deploy a strategy to start automated paper trading.</td></tr>}
            </tbody>
          </table>
        </Panel>

        <Panel tid="bot-logs" title="Engine Log" right={<Terminal className="w-3.5 h-3.5 text-slate-500" />}>
          <div className="h-56 overflow-y-auto p-2 font-mono text-[11px] space-y-0.5 bg-[#071525]">
            {logs.map((l) => (
              <div key={l.id} className={LOG_COLOR[l.level] || "text-slate-400"}>
                <span className="text-slate-600">{fmtTs(l.time)}</span> {l.message}
              </div>
            ))}
            {!logs.length && <div className="text-slate-600">$ waiting for signals…</div>}
          </div>
        </Panel>
      </div>

      <Panel tid="positions-panel" title={`Open Positions (${a.positions?.length || 0})`}>
        <div className="overflow-x-auto">
          <table className="tbl w-full">
            <thead><tr><th>Symbol</th><th>Mode</th><th>Source</th><th>Qty</th><th>Avg ₹</th><th>LTP ₹</th><th>Value</th><th>Unrealized P&L</th><th className="text-right">Action</th></tr></thead>
            <tbody>
              {(a.positions || []).map((p) => (
                <tr key={p.id} data-testid={`position-row-${p.symbol.toLowerCase()}`}>
                  <td className="text-slate-100">{p.symbol}</td><td className={p.mode === "LIVE" ? "text-[#FF3B30]" : "text-amber-400"}>{p.mode || "PAPER"}</td><td className="text-slate-400">{p.source_name}</td><td>{p.qty}</td>
                  <td>{num(p.avg_price)}</td><td>{num(p.ltp)}</td><td>{inr(p.market_value)}</td>
                  <td className={tone(p.unrealized_pnl)}>{inr(p.unrealized_pnl)} ({pct(p.unrealized_pct)})</td>
                  <td className="text-right">
                    <button data-testid={`position-close-${p.id}`} onClick={() => act(() => api.post(`/paper/positions/${p.id}/close`), `Squared off ${p.symbol}`)} className="btn-ghost h-6 px-2 hover:text-[#FF3B30]">
                      <X className="w-3 h-3" /> Exit
                    </button>
                  </td>
                </tr>
              ))}
              {!a.positions?.length && <tr><td colSpan={9} className="text-slate-500 font-sans py-6 text-center">No open positions</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel tid="orders-panel" title={`Order Book (${orders.length})`}>
        <div className="max-h-72 overflow-auto">
          <table className="tbl w-full">
            <thead><tr><th>Time</th><th>Symbol</th><th>Side</th><th>Qty</th><th>Price ₹</th><th>Mode</th><th>Source</th><th>Reason</th><th>P&L</th><th>Status</th></tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td>{fmtTs(o.time)}</td><td className="text-slate-100">{o.symbol}</td>
                  <td className={o.side === "BUY" ? "text-bull" : "text-bear"}>{o.side}</td><td>{o.qty}</td><td>{num(o.price)}</td>
                  <td className={o.mode === "LIVE" ? "text-[#FF3B30]" : "text-amber-400"}>{o.mode || "PAPER"}</td>
                  <td className="text-slate-400">{o.source_name}</td><td className="text-slate-400">{o.reason}</td>
                  <td className={tone(o.pnl)}>{o.pnl == null ? "—" : inr(o.pnl)}</td><td className="text-[#22A34A]">{o.status}{o.broker_order_id ? ` #${o.broker_order_id}` : ""}</td>
                </tr>
              ))}
              {!orders.length && <tr><td colSpan={10} className="text-slate-500 font-sans py-6 text-center">No orders yet</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
